import { useQuery } from '@tanstack/react-query';
import { useRef, useEffect } from 'react';
import axiosInstance from 'ns_libs/nsAxios';
import API_ENDPOINTS from 'services/urls';

export interface TaskStatusResponse {
    taskId: string;
    status: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';
    message: string;
    result?: {
        documentId: number;
        filename: string;
        gammaUrl: string;
        exportUrl: string;
    };
    currentStep?: number;
    error?: string;
}

// Maximum time to poll for task status (30 minutes)
const MAX_POLLING_DURATION_MS = 30 * 60 * 1000;

const fetchTaskStatus = async (
    taskId: string,
    organizationId: string,
): Promise<TaskStatusResponse> => {
    const endpoint = API_ENDPOINTS.TASKS.TASK_STATUS(taskId);
    const response = await axiosInstance.get(endpoint, {
        params: { organizationId },
    });
    return response.data;
};

interface UseTaskStatusOptions {
    taskId: string | null;
    organizationId: string;
    enabled?: boolean;
    onError?: (error: Error) => void;
}

const useTaskStatus = ({
    taskId,
    organizationId,
    enabled = true,
    onError,
}: UseTaskStatusOptions) => {
    const shouldPoll = enabled && !!taskId;
    const pollingStartTimeRef = useRef<number | null>(null);

    useEffect(() => {
        if (shouldPoll && !pollingStartTimeRef.current) {
            pollingStartTimeRef.current = Date.now();
        }
        return () => {
            if (!shouldPoll) {
                pollingStartTimeRef.current = null;
            }
        };
    }, [shouldPoll, taskId]);

    const query = useQuery({
        queryKey: ['taskStatus', taskId, organizationId],
        queryFn: () => fetchTaskStatus(taskId!, organizationId),
        enabled: shouldPoll,
        refetchInterval: query => {
            if (query.state.status === 'error') {
                return false;
            }
            const { data } = query.state;
            if (!data || data.status === 'SUCCESS' || data.status === 'FAILURE') {
                return false;
            }

            if (pollingStartTimeRef.current) {
                const elapsedTime = Date.now() - pollingStartTimeRef.current;
                if (elapsedTime > MAX_POLLING_DURATION_MS) {
                    console.warn(`Task ${taskId} polling exceeded maximum duration (30 minutes). Stopping polling.`);
                    if (onError) {
                        onError(new Error('Task polling timeout: The task is taking longer than expected. It may be stuck or still processing.'));
                    }
                    return false;
                }
            }

            return 3000;
        },
        retry: 3,
        retryDelay: 1000,
    });

    useEffect(() => {
        if (query.error && onError) {
            console.error('Task status polling failed:', query.error);
            onError(query.error as Error);
        }
    }, [query.error, onError]);

    return query;
};

export default useTaskStatus;
